import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Plus, X, Database, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { datasetsApi } from '@/services/api/datasets'
import type { Dataset } from '@/types/dataset'
import type { MultiSymbolDataset } from '@/types/multi-symbol'
import { showErrorToast } from '@/utils/error-handler'

interface DatasetSelectorProps {
  selectedDatasets: MultiSymbolDataset[]
  onDatasetsChange: (datasets: MultiSymbolDataset[]) => void
  maxDatasets?: number
}

export default function DatasetSelector({
  selectedDatasets,
  onDatasetsChange,
  maxDatasets = 20
}: DatasetSelectorProps) {
  const { t } = useTranslation()
  const [availableDatasets, setAvailableDatasets] = useState<Dataset[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)

  // Load available datasets
  useEffect(() => {
    const loadDatasets = async () => {
      setLoading(true)
      try {
        const response = await datasetsApi.list({
          limit: 100,
          status: 'ready'
        })
        setAvailableDatasets(response.datasets)
      } catch (error) {
        showErrorToast(error, t('multiSymbol.failedToLoadDatasets'))
      } finally {
        setLoading(false)
      }
    }

    loadDatasets()
  }, [])

  // Filter datasets based on search term
  const filteredDatasets = availableDatasets.filter(dataset =>
    dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dataset.symbol && dataset.symbol.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Check if a dataset is already selected
  const isDatasetSelected = (datasetId: string) => {
    return selectedDatasets.some(ds => ds.datasetId === datasetId)
  }


  // Remove a dataset from selection
  const removeDataset = (datasetId: string) => {
    onDatasetsChange(selectedDatasets.filter(ds => ds.datasetId !== datasetId))
  }


  // Get dataset details for display
  const getDatasetDetails = (datasetId: string) => {
    return availableDatasets.find(ds => ds.id === datasetId)
  }

  /** Normalized timeframe for comparison (same as backend). */
  const getTimeframe = (dataset: Dataset | undefined) => {
    if (!dataset) return '(unknown)'
    const tf = dataset.timeframe ?? dataset.metadata?.timeframe
    return (tf && String(tf).trim()) || '(unknown)'
  }

  /** Flat list of all symbols in the current selection (for duplicate check). */
  const getEffectiveSymbols = (selection: MultiSymbolDataset[], available: Dataset[]) => {
    const out: string[] = []
    for (const sel of selection) {
      const details = available.find(ds => ds.id === sel.datasetId)
      if ('symbols' in sel && Array.isArray(sel.symbols)) {
        sel.symbols.forEach(s => out.push(String(s).trim()))
      } else {
        const sym = sel.symbol ?? details?.symbol ?? details?.name ?? ''
        if (sym) out.push(String(sym).trim())
      }
    }
    return out
  }

  const selectedTimeframe = selectedDatasets.length > 0
    ? getTimeframe(getDatasetDetails(selectedDatasets[0].datasetId))
    : null
  const effectiveSymbols = getEffectiveSymbols(selectedDatasets, availableDatasets)
  const duplicateSymbols = effectiveSymbols.filter((s, i) => effectiveSymbols.indexOf(s) !== i)
  const hasDuplicates = [...new Set(duplicateSymbols)].length > 0

  // Add a dataset to selection (with same-timeframe and no-duplicate-symbol checks)
  const addDatasetWithValidation = (dataset: Dataset) => {
    if (selectedDatasets.length >= maxDatasets) {
      showErrorToast(t('multiSymbol.maxDatasetsAllowed', { max: maxDatasets }))
      return
    }
    if (isDatasetSelected(dataset.id)) {
      showErrorToast(t('multiSymbol.datasetAlreadySelected'))
      return
    }

    const newTimeframe = getTimeframe(dataset)
    if (selectedTimeframe != null && newTimeframe !== selectedTimeframe) {
      showErrorToast(t('multiSymbol.sameTimeframeRequired', { selected: selectedTimeframe, this: newTimeframe }))
      return
    }

    const isPartitioned = dataset.metadata?.partitionStrategy === 'by_symbol'
    const newSymbols: string[] = isPartitioned
      ? (dataset.metadata?.symbols as string[]) || []
      : [dataset.symbol || dataset.name].filter(Boolean)

    const existingSet = new Set(effectiveSymbols)
    const overlap = newSymbols.filter(s => existingSet.has(String(s).trim()))
    if (overlap.length > 0) {
      showErrorToast(t('multiSymbol.duplicateSymbolsNotAllowed', { symbols: [...new Set(overlap)].join(', ') }))
      return
    }

    const newDataset: MultiSymbolDataset = {
      datasetId: dataset.id,
      ...(isPartitioned ? { symbols: (dataset.metadata?.symbols as string[]) || [] } : { symbol: dataset.symbol || dataset.name }),
    }
    onDatasetsChange([...selectedDatasets, newDataset])
  }

  return (
    <div className="space-y-6">
      {/* Selected Datasets */}
      {selectedDatasets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('multiSymbol.selectedDatasets')} ({selectedDatasets.length}/{maxDatasets})
              {selectedTimeframe != null && (
                <Badge variant="outline" className="font-normal">
                  {t('multiSymbol.timeframe')}: {selectedTimeframe}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {hasDuplicates && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                {t('multiSymbol.duplicateSymbolsDetected')}
              </div>
            )}
            {selectedDatasets.map((dataset) => {
              const details = getDatasetDetails(dataset.datasetId)
              const isPartitioned = 'symbols' in dataset
              const tf = details ? getTimeframe(details) : null

              return (
                <div
                  key={dataset.datasetId}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <Database className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {details?.name || dataset.datasetId}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isPartitioned ? (
                          <span>
                            {t('multiSymbol.partitioned')} • {Array.isArray(dataset.symbols) ? dataset.symbols.length : 0} {t('multiSymbol.symbols')}
                          </span>
                        ) : (
                          <span>
                            {t('multiSymbol.singleSymbol')} • {dataset.symbol}
                          </span>
                        )}
                        {tf && <span> • {tf}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isPartitioned && dataset.symbols && (
                      <Badge variant="secondary">
                        {Array.isArray(dataset.symbols) ? dataset.symbols.length : 0} {t('multiSymbol.symbols')}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDataset(dataset.datasetId)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Dataset Browser */}
      <Card>
        <CardHeader>
          <CardTitle>{t('multiSymbol.availableDatasets')}</CardTitle>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder={t('multiSymbol.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value || '')}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('multiSymbol.loadingDatasets')}
            </div>
          ) : filteredDatasets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? t('multiSymbol.noDatasetsFound') : t('multiSymbol.noDatasetsAvailable')}
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredDatasets.map((dataset) => {
                const isSelected = isDatasetSelected(dataset.id)
                const isPartitioned = dataset.metadata?.partitionStrategy === 'by_symbol'

                return (
                  <div
                    key={dataset.id}
                    className={`flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors ${
                      isSelected ? 'bg-muted border-primary' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{dataset.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {isPartitioned ? (
                            <span>
                              {t('multiSymbol.partitioned')} • {(dataset.metadata?.symbols as string[])?.length || 0} {t('multiSymbol.symbols')}
                            </span>
                          ) : (
                            <span>
                              {t('multiSymbol.singleSymbol')} • {dataset.symbol || t('multiSymbol.unknown')}
                            </span>
                          )}
                          <span> • {getTimeframe(dataset)}</span>
                          {dataset.rowCount && (
                            <span> • {dataset.rowCount.toLocaleString()} {t('multiSymbol.rows')}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button
                      variant={isSelected ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => isSelected ? removeDataset(dataset.id) : addDatasetWithValidation(dataset)}
                      disabled={isSelected || selectedDatasets.length >= maxDatasets}
                    >
                      {isSelected ? (
                        <>
                          <X className="w-4 h-4 mr-1" />
                          {t('multiSymbol.remove')}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-1" />
                          {t('multiSymbol.add')}
                        </>
                      )}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}